import { dialog } from '../services/dialog'
import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Clock, Headphones, BarChart3, Calendar, Shield, UserX, ArrowRightLeft, GraduationCap, ChevronRight, CheckSquare, Network, Volume2, Activity } from 'lucide-react'
import api, { voiceSessionApi, channelApi, serverApi, peopleApi } from '../services/api'
import { mediaUrl } from '../services/config'
import type { OrgPayload } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useVoicePresence } from '../hooks/useVoicePresence'
import { useServerStore } from '../shell/stores/serverStore'
import { PageShell } from '../components/layout/PageShell'
import { PageBrow } from '../components/layout/PageBrow'
import { SectionCard, KpiCard, KpiRow, DataTable, type Column, EmptyState } from '../components/patterns'
import { displayInitial } from '../utils/displayInitial'
import { formatDuration } from '../utils/formatDuration'
import { emotionZone } from '../utils/emotionZone'

interface LearningItem { id: string; type: 'course' | 'test'; title: string; score: number | null; attempts: number; status: string }

interface VoiceChannel {
  id: string
  name: string
}

interface AdminContext {
  channelId?: string
  serverId?: string
  canManage?: boolean
  voiceChannels?: VoiceChannel[]
}

// GET /api/taskboards/users/:userId/assigned-items → tasks assigned to the
// viewed user, grouped by board, scoped to boards the REQUESTER can access
// (all if the requester is a platform admin; otherwise just their boards).
interface AssignedItem {
  id: string
  title: string
  statusId: string | null
  status: { name: string; category: string; color: string | null } | null
  priority: string | null
  externalKey: string | null
  boardId: string
}
interface AssignedGroup {
  board: { id: string; name: string; color: string | null; serverId: string }
  items: AssignedItem[]
}

// Priority → dot colour, label-tolerant so Jira/Monday names map without an
// exact match. High/blocker/critical = red, medium = amber, low = sky, else gray.
function priorityDotClass(priority: string): string {
  const p = priority.toLowerCase()
  if (p.includes('block') || p.includes('critical') || p.includes('high') || p.includes('urgent') || p.includes('highest')) return 'bg-red-500'
  if (p.includes('medium') || p.includes('normal')) return 'bg-amber-500'
  if (p.includes('low') || p.includes('minor') || p.includes('lowest')) return 'bg-sky-500'
  return 'bg-muted-foreground/50'
}

export default function UserPublicProfilePage() {
  const navigate = useNavigate()
  const { userId } = useParams<{ userId: string }>()
  // Live voice presence — "in <channel> · Join" if this user is currently in a
  // room I can join, else "in voice · no access".
  const { byUserId: voicePresenceByUserId } = useVoicePresence()
  const voicePresence = userId ? voicePresenceByUserId.get(userId) : undefined
  const joinTheirChannel = () => {
    if (!voicePresence?.canJoin || !voicePresence.channelId) return
    useVoiceStore.getState().setPendingAutoJoin(voicePresence.channelId)
    navigate(`/servers/${voicePresence.serverId ?? '@me'}/channels/${voicePresence.channelId}`)
  }
  const location = useLocation()
  const adminCtx = (location.state || {}) as AdminContext

  const isAdmin = useAuthStore((s) => s.user?.isAdmin)
  const { activeServerId, servers } = useServerStore()
  const serverId = activeServerId ?? servers[0]?.id

  const [data, setData] = useState<any>(null)
  const [learning, setLearning] = useState<LearningItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [isKicking, setIsKicking] = useState(false)
  const [isBanning, setIsBanning] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [showMoveDropdown, setShowMoveDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assigned, setAssigned] = useState<AssignedGroup[]>([])
  const [assignedLoading, setAssignedLoading] = useState(true)
  const [orgData, setOrgData] = useState<OrgPayload | null>(null)
  const [activityPeriod, setActivityPeriod] = useState<'mtd' | '30d' | '7d'>('mtd')
  const [emotion, setEmotion] = useState<null | { index: number; posSpikes: number; negSpikes: number; depthMax: number; lastAt: string | null; spikes: { incidentId: string; title: string; polarity: string; severity: string; source: string; whenIso: string }[] }>(null)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    api.get(`/workspace/service/person/${userId}`)
      .then((r) => { if (!cancelled) setEmotion(r.data?.index != null ? r.data : null) })
      .catch(() => { if (!cancelled) setEmotion(null) }) // 403 → section hidden
    return () => { cancelled = true }
  }, [userId])

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    setError(null)
    voiceSessionApi
      .userStats(userId)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [userId])

  // Learning section — the user's courses/tests with score%+tries. The server
  // filters by the VIEWER's access (admin sees all; others only what they can view).
  useEffect(() => {
    if (!userId || !serverId) return
    api.get<LearningItem[]>(`/learning/users/${userId}/results`, { params: { serverId } })
      .then((r) => setLearning(r.data))
      .catch(() => setLearning([]))
  }, [userId, serverId])

  const openLearning = (it: LearningItem) => {
    if (!isAdmin) return
    navigate(it.type === 'test'
      ? `/workspace/learning?tab=tests&studentsTest=${it.id}`
      : `/workspace/learning?tab=courses&course=${it.id}&students=${it.id}`)
  }

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    setAssignedLoading(true)
    api
      .get<AssignedGroup[]>(`/taskboards/users/${userId}/assigned-items`)
      .then((res) => { if (!cancelled) setAssigned(res.data) })
      .catch(() => { if (!cancelled) setAssigned([]) })
      .finally(() => { if (!cancelled) setAssignedLoading(false) })
    return () => { cancelled = true }
  }, [userId])

  // Org chart — fetch once; tolerate failure (null → section hidden)
  useEffect(() => {
    if (!userId) return
    let cancelled = false
    peopleApi
      .org()
      .then((res) => { if (!cancelled) setOrgData(res.data) })
      .catch(() => { /* silently omit reporting-line section */ })
    return () => { cancelled = true }
  }, [userId])

  // Reporting-line derivations
  const reportingLine = useMemo(() => {
    if (!orgData || !userId) return null
    const personMap = new Map<string, OrgPayload['people'][number]>()
    for (const p of orgData.people) personMap.set(p.id, p)

    const profilePerson = personMap.get(userId)
    if (!profilePerson) return null

    // Walk up managerId chain; cap at 20 hops to guard cycles
    const chain: OrgPayload['people'][number][] = []
    let cur = profilePerson.managerId
    const seen = new Set<string>()
    while (cur && !seen.has(cur) && chain.length < 20) {
      seen.add(cur)
      const mgr = personMap.get(cur)
      if (!mgr) break
      chain.push(mgr)
      cur = mgr.managerId
    }

    // Departments this user heads or reports-to
    const manages = orgData.departments.filter(
      (d) => d.reportsToUserId === userId || d.headUserId === userId,
    )

    // People whose direct manager is this user
    const directReports = orgData.people.filter((p) => p.managerId === userId)

    return { chain, manages, directReports }
  }, [orgData, userId])

  // Activity period filter
  const filteredDailyStats = useMemo(() => {
    const raw: Array<{ date: string; duration: number; sessions: number }> =
      (data?.dailyStats) ?? []
    if (raw.length === 0) return raw

    const today = new Date()
    let threshold: string
    if (activityPeriod === 'mtd') {
      // First day of current month
      threshold = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
    } else if (activityPeriod === '30d') {
      const d = new Date(today)
      d.setDate(d.getDate() - 29)
      threshold = d.toISOString().slice(0, 10)
    } else {
      // 7d
      const d = new Date(today)
      d.setDate(d.getDate() - 6)
      threshold = d.toISOString().slice(0, 10)
    }
    return raw.filter((entry) => entry.date >= threshold)
  }, [data, activityPeriod])

  const handleKick = async () => {
    if (!adminCtx.channelId || !userId) return
    if (!(await dialog.confirm({ message: 'Kick user from channel?', tone: 'destructive', confirmLabel: 'Kick' }))) return
    setIsKicking(true)
    setError(null)
    try {
      await channelApi.kickUser(adminCtx.channelId, userId)
      navigate(-1)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to kick')
    } finally {
      setIsKicking(false)
    }
  }

  const handleBan = async () => {
    if (!adminCtx.serverId || !userId) return
    if (!(await dialog.confirm({ message: 'Ban user from server?', tone: 'destructive', confirmLabel: 'Ban' }))) return
    setIsBanning(true)
    setError(null)
    try {
      await serverApi.banMember(adminCtx.serverId, { userId })
      navigate(-1)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to ban')
    } finally {
      setIsBanning(false)
    }
  }

  const handleMove = async (targetChannelId: string) => {
    if (!adminCtx.channelId || !userId) return
    setIsMoving(true)
    setError(null)
    try {
      await channelApi.moveUser(adminCtx.channelId, userId, targetChannelId)
      navigate(-1)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move')
    } finally {
      setIsMoving(false)
      setShowMoveDropdown(false)
    }
  }

  const availableChannels = (adminCtx.voiceChannels || []).filter((ch) => ch.id !== adminCtx.channelId)

  const closeFn = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }

  // Top Channels DataTable columns
  const topChannelColumns: Column<any>[] = [
    {
      key: 'channel',
      header: 'Channel',
      render: (ch) => <span className="text-sm text-foreground truncate">{ch.channelName}</span>,
    },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      render: (ch) => <span className="text-xs text-muted-foreground">{formatDuration(ch.duration)}</span>,
    },
    {
      key: 'sessions',
      header: 'Sessions',
      align: 'right',
      render: (ch) => <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{ch.sessions} visits</span>,
    },
  ]

  // Recent Sessions DataTable columns
  const recentSessionColumns: Column<any>[] = [
    {
      key: 'channel',
      header: 'Channel',
      render: (s) => <span className="text-sm text-foreground truncate">{s.channelName}</span>,
    },
    {
      key: 'server',
      header: 'Server',
      render: (s) => <span className="text-xs text-muted-foreground">{s.serverName}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      render: (s) => <span className="text-xs text-muted-foreground">{new Date(s.startedAt).toLocaleDateString()}</span>,
    },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      render: (s) => <span className="text-xs text-muted-foreground font-mono">{formatDuration(s.duration || 0)}</span>,
    },
  ]

  // Learning DataTable columns
  const learningColumns: Column<LearningItem>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (it) => (
        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{it.type}</span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (it) => <span className="text-sm text-foreground truncate">{it.title}</span>,
    },
    {
      key: 'score',
      header: 'Score%',
      align: 'right',
      render: (it) => <span className="text-xs tabular-nums text-foreground">{it.score != null ? `${it.score}%` : '-'}</span>,
    },
    {
      key: 'attempts',
      header: 'Attempts',
      align: 'right',
      render: (it) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {it.attempts} {it.attempts === 1 ? 'try' : 'tries'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      render: (it) => {
        const statusCls = it.status === 'passed' ? 'text-emerald-400' : it.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
        const statusLabel = it.status === 'passed' ? 'Passed' : it.status === 'failed' ? 'Failed' : 'In progress'
        return <span className={`text-xs ${statusCls}`}>{statusLabel}</span>
      },
    },
  ]

  if (!userId) {
    return (
      <PageShell brow={<PageBrow left={<span className="text-xl font-semibold">User profile</span>} onBack={closeFn} />}>
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-destructive text-lg">User not found</div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell
      brow={
        <PageBrow
          left={
            <div>
              <div className="text-base font-semibold text-foreground leading-tight">User profile</div>
              <div className="text-xs text-muted-foreground">Public profile and voice activity</div>
            </div>
          }
          onBack={closeFn}
        />
      }
    >
      <div className="mx-auto max-w-3xl p-6 space-y-6">
        {loading && <div className="text-muted-foreground text-center py-8">Loading...</div>}
        {!loading && !data && <div className="text-muted-foreground text-center py-8">Failed to load profile</div>}

        {!loading && data && (
          <>
            {/* Header card — avatar / name / admin actions, no section title */}
            <SectionCard title={null as any}>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-foreground text-2xl font-bold overflow-hidden">
                  {data.user.avatarUrl ? (
                    <img src={mediaUrl(data.user.avatarUrl)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    displayInitial(data.user.username)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-bold text-foreground">{data.user.username}</div>
                  {data.user.position && (
                    <div className="text-sm text-muted-foreground">{data.user.position}</div>
                  )}
                  {data.birthday && (
                    <div className="text-sm text-muted-foreground">
                      {/* birthday is date-only (UTC midnight) — format in UTC or negative-offset
                          timezones (e.g. America/New_York) render the previous day */}
                      🎂 {new Date(data.birthday).toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' })}
                      {typeof data.age === 'number' ? ` · ${data.age}` : ''}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground capitalize">
                    {data.user.status?.toLowerCase().replace(/_/g, ' ')}
                  </div>
                  {voicePresence?.inVoice && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                      <Volume2 size={13} className="text-emerald-500 shrink-0" />
                      {voicePresence.canJoin ? (
                        <>
                          <span className="text-muted-foreground truncate">
                            In <span className="text-foreground font-medium">{voicePresence.channelName}</span>
                          </span>
                          <button
                            onClick={joinTheirChannel}
                            className="ml-1 shrink-0 px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold hover:bg-primary/25 transition"
                          >
                            Join
                          </button>
                        </>
                      ) : (
                        <span className="text-muted-foreground italic">In a voice channel — you don&apos;t have access</span>
                      )}
                    </div>
                  )}
                </div>

                {adminCtx.canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    {adminCtx.channelId && (
                      <button
                        onClick={handleKick}
                        disabled={isKicking}
                        title="Kick from channel"
                        className="p-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition disabled:opacity-50"
                      >
                        <UserX size={16} />
                      </button>
                    )}
                    {adminCtx.serverId && (
                      <button
                        onClick={handleBan}
                        disabled={isBanning}
                        title="Ban on server"
                        className="p-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition disabled:opacity-50"
                      >
                        <Shield size={16} />
                      </button>
                    )}
                    {adminCtx.channelId && availableChannels.length > 0 && (
                      <div className="relative">
                        <button
                          onClick={() => setShowMoveDropdown((v) => !v)}
                          disabled={isMoving}
                          title="Move to another channel"
                          className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition disabled:opacity-50"
                        >
                          <ArrowRightLeft size={16} />
                        </button>
                        {showMoveDropdown && (
                          <div className="absolute right-0 top-10 z-50 bg-card rounded shadow-lg border border-border py-1 w-48 max-h-48 overflow-y-auto">
                            <div className="px-3 py-1 text-[10px] text-muted-foreground uppercase font-semibold">Move to</div>
                            {availableChannels.map((ch) => (
                              <button
                                key={ch.id}
                                onClick={() => handleMove(ch.id)}
                                className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent transition truncate"
                              >
                                {ch.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SectionCard>

            {error && (
              <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm rounded-lg">{error}</div>
            )}

            {/* Reporting line — org hierarchy for this person */}
            {reportingLine && (
              <SectionCard title="Reporting line" icon={<Network size={14} />}>
                <div className="space-y-2.5 text-sm">
                  {/* Reports-to chain */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Reports to:</span>
                    {reportingLine.chain.length === 0 ? (
                      <span className="text-foreground">Top of the org chart</span>
                    ) : (
                      reportingLine.chain.map((mgr, idx) => {
                        const name = [mgr.firstName, mgr.lastName].filter(Boolean).join(' ') || mgr.email
                        return (
                          <span key={mgr.id} className="flex items-center gap-1">
                            {idx > 0 && <ChevronRight size={12} className="text-muted-foreground shrink-0" />}
                            <button
                              onClick={() => navigate(`/users/${mgr.id}`)}
                              className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition"
                            >
                              {name}
                            </button>
                          </span>
                        )
                      })
                    )}
                  </div>

                  {/* Managed departments */}
                  {reportingLine.manages.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-muted-foreground shrink-0">
                        Leads {reportingLine.manages.length} {reportingLine.manages.length === 1 ? 'department' : 'departments'}:
                      </span>
                      {reportingLine.manages.map((dept, idx) => (
                        <span key={dept.id} className="text-foreground">
                          {dept.name}{idx < reportingLine.manages.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Direct reports */}
                  {reportingLine.directReports.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-muted-foreground shrink-0">
                        Direct reports ({reportingLine.directReports.length}):
                      </span>
                      {reportingLine.directReports.map((rep) => {
                        const name = [rep.firstName, rep.lastName].filter(Boolean).join(' ') || rep.email
                        return (
                          <button
                            key={rep.id}
                            onClick={() => navigate(`/users/${rep.id}`)}
                            className="px-2 py-0.5 rounded-md bg-muted/60 text-foreground text-xs font-medium hover:bg-muted transition"
                          >
                            {name}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* KPI row — Sessions / Total time / Channels */}
            <KpiRow className="grid-cols-3 lg:grid-cols-3">
              <KpiCard
                label="Sessions"
                value={data.totalSessions}
                icon={<Headphones size={18} />}
                accent="text-primary"
              />
              <KpiCard
                label="Total time"
                value={formatDuration(data.totalDuration)}
                icon={<Clock size={18} />}
                accent="text-emerald-500"
              />
              <KpiCard
                label="Channels"
                value={data.topChannels?.length || 0}
                icon={<BarChart3 size={18} />}
                accent="text-amber-500"
              />
            </KpiRow>

            {emotion && (emotion.lastAt != null || emotion.posSpikes > 0 || emotion.negSpikes > 0 || emotion.spikes.length > 0) && (
              <SectionCard title="Emotional state" icon={<Activity size={14} />}>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-3xl font-bold tabular-nums ${emotionZone(emotion.index).text}`}>{emotion.index}</span>
                  <span className="flex-1">
                    <span className="block h-2 w-full rounded-full bg-muted overflow-hidden">
                      <span className={`block h-full rounded-full ${emotionZone(emotion.index).bar}`} style={{ width: `${Math.max(0, Math.min(100, emotion.index))}%` }} />
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {emotion.negSpikes} negative · {emotion.posSpikes} positive · depth {emotion.depthMax.toFixed(2)}
                    </span>
                  </span>
                </div>
                {emotion.spikes.length > 0 && (
                  <div className="space-y-1">
                    {emotion.spikes.map((s) => (
                      <button key={s.incidentId} onClick={() => navigate('/workspace/service/incident/' + s.incidentId)}
                        className="w-full text-left flex items-center gap-2 rounded-md border border-border bg-card hover:bg-muted px-2 py-1.5 transition">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.polarity === 'negative' ? 'bg-destructive' : 'bg-emerald-500'}`} />
                        <span className="text-sm text-foreground truncate flex-1">{s.title}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{new Date(s.whenIso).toLocaleDateString()}</span>
                      </button>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Learning — courses & tests this user has taken. Server filters
                by the viewer's access (admin = all). %+tries are public; admin
                can click through to the answers. */}
            {learning && learning.length > 0 && (
              <SectionCard title="Learning" icon={<GraduationCap size={14} />} noBodyPadding>
                <DataTable<LearningItem>
                  columns={learningColumns}
                  rows={learning}
                  rowKey={(it) => `${it.type}-${it.id}`}
                  onRowClick={isAdmin ? openLearning : undefined}
                  empty={<EmptyState title="No learning results" />}
                />
                {isAdmin && <p className="text-[10px] text-muted-foreground px-5 py-2">Click an item to see this user's answers.</p>}
              </SectionCard>
            )}

            {/* Top channels */}
            {data.topChannels?.length > 0 && (
              <SectionCard title="Top Channels" icon={<BarChart3 size={14} />} noBodyPadding>
                <DataTable<any>
                  columns={topChannelColumns}
                  rows={data.topChannels.slice(0, 10)}
                  rowKey={(ch) => ch.channelId}
                  empty={<EmptyState title="No channel data" />}
                />
              </SectionCard>
            )}

            {/* Daily activity */}
            {data.dailyStats?.length > 0 && (
              <SectionCard
                title={
                  <span>
                    Daily Activity
                    <span className="ml-1 font-normal text-muted-foreground text-[11px]">
                      &middot; {activityPeriod === 'mtd' ? 'Month to date' : activityPeriod === '30d' ? '30 days' : '7 days'}
                    </span>
                  </span>
                }
                icon={<Calendar size={14} />}
                action={
                  <div className="flex items-center gap-1">
                    {(['mtd', '30d', '7d'] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setActivityPeriod(p)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition ${
                          activityPeriod === p
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {p === 'mtd' ? 'Month to date' : p === '30d' ? '30 days' : '7 days'}
                      </button>
                    ))}
                  </div>
                }
              >
                {filteredDailyStats.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-6">No activity in this period</div>
                ) : (
                  <div className="flex items-end gap-1 h-24">
                    {(() => {
                      const max = Math.max(...filteredDailyStats.map((x) => x.duration))
                      return filteredDailyStats.map((d) => {
                        const h = max > 0 ? (d.duration / max) * 100 : 0
                        return (
                          <div
                            key={d.date}
                            className="flex-1 flex flex-col items-center gap-1"
                            title={`${d.date}: ${formatDuration(d.duration)}`}
                          >
                            <div
                              className="w-full bg-primary rounded-t-sm"
                              style={{ height: `${h}%`, minHeight: '2px' }}
                            />
                            <span className="text-[8px] text-muted-foreground rotate-45 origin-left translate-y-1">
                              {d.date.slice(5)}
                            </span>
                          </div>
                        )
                      })
                    })()}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Recent sessions */}
            {data.recentSessions?.length > 0 && (
              <SectionCard title="Recent Sessions" icon={<Clock size={14} />} noBodyPadding>
                <DataTable<any>
                  columns={recentSessionColumns}
                  rows={data.recentSessions}
                  rowKey={(s) => s.id}
                  empty={<EmptyState title="No recent sessions" />}
                />
              </SectionCard>
            )}
          </>
        )}

        {/* Assigned tasks — independent of the voice-stats fetch above. */}
        <SectionCard title="Assigned tasks" icon={<CheckSquare size={14} />}>
          {assignedLoading ? (
            <div className="text-sm text-muted-foreground text-center py-6">Loading...</div>
          ) : assigned.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">No assigned tasks on boards you can see</div>
          ) : (
            <div className="space-y-5">
              {assigned.map((group) => (
                <div key={group.board.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: group.board.color || '#9ca3af' }} />
                    <span className="text-sm font-semibold text-foreground truncate">{group.board.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">{group.items.length}</span>
                  </div>
                  <div className="space-y-1.5">
                    {group.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => navigate(`/workspace/tasks/boards/${item.boardId}?item=${item.id}`)}
                        className="w-full flex items-center gap-2.5 bg-muted/40 hover:bg-muted/70 rounded-lg px-3 py-2 text-left transition"
                      >
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.status?.color || '#9ca3af' }} title={item.status?.name ?? undefined} />
                        {item.status && <span className="text-[11px] text-muted-foreground shrink-0 max-w-[100px] truncate">{item.status.name}</span>}
                        {item.priority && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${priorityDotClass(item.priority)}`} title={item.priority} />}
                        <span className="text-sm text-foreground truncate flex-1 min-w-0">{item.title}</span>
                        {item.externalKey && <span className="text-[10px] font-mono text-muted-foreground bg-background px-1.5 py-0.5 rounded shrink-0">{item.externalKey}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </PageShell>
  )
}
